import "./CarCarouselImagesComponentStyles.scss";
import { FC, useEffect, useRef, useState } from "react";
import CarCarouselImagesComponentProps from "./CarCarouselImagesComponentProps";
import Carousel from "react-multi-carousel";
import { AiOutlineArrowRight, AiOutlineArrowLeft } from "react-icons/ai";
import { SlideShowImage } from "../slideShowImage/slideShowImage";
import ZoomedCarComponent from "../../pages/car/detailedCar/component/zommedCar/ZoomedCarComponent";
import { VscChromeClose } from "react-icons/vsc";
import { ImageWithBlurPlaceholder } from "./ImagePlaceholderComponent";
import { buildSrcSet, variantsFor } from "../../../../utils/image";
import { trackEvent } from "../../../../lib/analytics";
import VideoButton from "../VideoButton/VideoButton";
import { IS_SCREENS_MODE } from "../../../../config/appMode";

const ThumbnailImage: FC<{ src: string; alt: string; srcSet?: string }> = ({ src, alt, srcSet }) => {
    const [loaded, setLoaded] = useState(false);
    return (
        <div style={{ position: "relative", width: 130, height: 70, margin: 10, borderRadius: 15, overflow: "hidden", flexShrink: 0 }}>
            {!loaded && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)",
                        backgroundSize: "200% 100%",
                        animation: "skeleton-shimmer 1.4s infinite ease-in-out",
                        borderRadius: 15,
                    }}
                />
            )}
            <img
                src={src}
                srcSet={srcSet}
                // La miniatura se muestra a 130px: con srcSet el navegador baja la variante
                // chica en vez del original (mejora de performance).
                sizes={srcSet ? "130px" : undefined}
                alt={alt}
                loading="lazy"
                onLoad={() => setLoaded(true)}
                style={{
                    width: 130,
                    height: 70,
                    objectFit: "cover",
                    borderRadius: 15,
                    cursor: "pointer",
                    opacity: loaded ? 1 : 0,
                    transition: "opacity 0.35s ease-out",
                    display: "block",
                }}
            />
        </div>
    );
};

// En modo infinito react-multi-carousel clona 2 * slidesToShow items al inicio y al final,
// así que el índice que reporta el carousel viene corrido respecto al índice real de `images`.
// Aquí slidesToShow siempre es 1 (ver `responsive`), por lo que el desfase es 2.
const CLONES_OFFSET = 2;

const CarCarouselImagesComponent: FC<CarCarouselImagesComponentProps> = ({ images, car }) => {
    const [imageShowing, setImageShowing] = useState<number>(0);
    const carouselRef = useRef<Carousel>(null);
    const imgContainerRef = useRef<HTMLDivElement>(null);
    const [postionMouse, setPositionMouse] = useState<{ x: number; y: number } | null>(null);
    const [rect, setRect] = useState<DOMRect | null>(null);
    const [openZoom, setOpenZoom] = useState<boolean>(false);
    const [openable, setOpenable] = useState<boolean>(true);

    const _toRealIndex = (slide: number) => {
        if (images.length === 0) return 0;
        return (((slide - CLONES_OFFSET) % images.length) + images.length) % images.length;
    };

    const _onChange = (slide: number) => {
        setOpenable(false);
        setImageShowing(_toRealIndex(slide));
    };

    const _handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!openable) {
            setPositionMouse(null);
            // setPositionMouse({ x: 0, y: 0 });
            return;
        }
        if (window.innerWidth < 768 || imgContainerRef.current == null || rect == null) {
            setPositionMouse(null);
            // setPositionMouse({ x: 0, y: 0 });
            return;
        }
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;
        if (x < 0 || y < 0 || x > rect.width - 1 || y > rect.height - 1) {
            setPositionMouse(null);
            // setPositionMouse({ x: 0, y: 0 });
        } else {
            x = x > rect.height * 0.2 ? x : rect.height * 0.2;
            x = x < rect.width - rect.height * 0.2 ? x : rect.width - rect.height * 0.2;
            y = y > rect.height * 0.2 ? y : rect.height * 0.2;
            y = y < rect.height * 0.8 ? y : rect.height * 0.8;
            setPositionMouse({ x, y });
        }
    };

    const _calculateLeftMarginZoom = () => {
        if (postionMouse && rect) {
            if (postionMouse.x < rect.height * 0.25) return 0;
            else if (postionMouse.x > rect.width - rect.height * 0.2) return -(rect.width * 2) + rect.height * 0.25 + 2;
            // else return (postionMouse.x - rect.height * 0.25) * (-(rect.width * 2 - rect.height * 0.25 * 2) / (rect.width - rect.height * 0.5));
            else {
                //m = (y2 - y1) / (x2 - x1)
                const x1 = rect.height * 0.25;
                const y1 = 0;
                const x2 = rect.width - rect.height * 0.2;
                const y2 = -(rect.width * 2) + rect.height * 0.25;
                const m = (y2 - y1) / (x2 - x1);
                const b = y1 - m * x1;
                return m * postionMouse.x + b + 17;
            }
        }
        return 0;
    };

    const _calculateTopMarginZoom = () => {
        if (postionMouse && rect) {
            if (postionMouse.y < rect.height * 0.2) return 0;
            if (postionMouse.y > rect.height * 0.8) return -rect.height * 0.6 * 2.5;
            return -(postionMouse.y * 2.5) + rect.height * 0.5;
        }
        return 0;
    };

    const _handleGoToImage = (index: number) => {
        carouselRef.current!.goToSlide(index + CLONES_OFFSET);
    };

    // El carousel es infinito: desde la última imagen se pasa a la primera y viceversa.
    // El 0 es la cantidad de slides "arrastrados" (solo aplica al swipe), avanza de a uno.
    const _handleNextImage = () => {
        carouselRef.current!.next(0);
    };

    const _handlePrevImage = () => {
        carouselRef.current!.previous(0);
    };

    const _showCarousel = () => setOpenZoom(true);

    // Solo la apertura del zoom, no cada foto que se pase dentro de el.
    const _abrirZoom = (habilitado: boolean) => {
        if (!habilitado) return;
        trackEvent("vehiculo_foto_ampliada", { vehiculo_id: String(car.id) });
        setOpenZoom(true);
    };

    useEffect(() => {
        if (imgContainerRef.current) {
            setRect(imgContainerRef.current.getBoundingClientRect());
        }
    }, [imgContainerRef.current]);

    return (
        <div className="car_carousel_images_component">
            {openZoom && (
                <div className="bg_slide">
                    <div
                        onClick={() => setOpenZoom(false)}
                        className="closed"
                        style={{ position: "absolute", top: 20, zIndex: 1, margin: "auto" }}
                    >
                        <VscChromeClose />
                    </div>
                    <div className="slide_container">
                        <SlideShowImage
                            images={images}
                            car={car}
                            imageShowing={imageShowing}
                            setOpenZoom={setOpenZoom}
                        />
                    </div>
                </div>
            )}
            <div className="container_image_showing" ref={imgContainerRef}>
                <div className="position-relative">
                    {postionMouse && rect && (
                        <div className="zoom_shower">
                            <img
                                src={images[imageShowing]}
                                // La lupa amplia a rect.width*2.5: con srcSet el navegador
                                // trae la variante mas grande disponible para que el zoom
                                // sea lo mas nitido posible (limitado por el original).
                                srcSet={buildSrcSet(variantsFor(car.imageSrcsetByKey, images[imageShowing]))}
                                sizes={
                                    buildSrcSet(variantsFor(car.imageSrcsetByKey, images[imageShowing]))
                                        ? `${Math.round(rect.width * 2.5)}px`
                                        : undefined
                                }
                                style={{
                                    marginLeft: _calculateLeftMarginZoom(),
                                    marginTop: _calculateTopMarginZoom(),
                                    width: rect.width * 2.5,
                                    height: rect.height * 2.5,
                                }}
                                alt={car?.name}
                                title={car?.name}
                            />
                        </div>
                    )}

                    <Carousel
                        ref={carouselRef}
                        arrows={false}
                        infinite={true}
                        draggable={true}
                        swipeable={true}
                        slidesToSlide={1}
                        afterChange={() => setOpenable(true)}
                        responsive={{
                            mobile: {
                                breakpoint: { max: 3000, min: 0 },
                                items: 1,
                                slidesToSlide: 1, // optional, default to 1.
                            },
                        }}
                        beforeChange={(slide, _) => _onChange(slide)}
                    >
                        {images.map((image, index) => (
                            <div className="position-relative" key={index}>
                                <div
                                    className="zoom_wrapper"
                                    style={{ minHeight: 500, ...(IS_SCREENS_MODE && { maxHeight: "70vh" }) }}
                                    onMouseOut={() => setPositionMouse(null)}
                                    onMouseMove={_handleMouseMove}
                                    onClick={() => _abrirZoom(openable)}
                                >
                                    {postionMouse && (
                                        <div
                                            className="zoom_loop"
                                            style={{ left: postionMouse?.x, top: postionMouse?.y }}
                                        >
                                            <div className="zoom_box"></div>
                                        </div>
                                    )}
                                </div>
                                <div className="carousel_thumbail" key={index} onClick={() => _abrirZoom(openable)}>
                                    <div className="position-relative">
                                        <ImageWithBlurPlaceholder image={image} car={car} isFirst={index === 0} />
                                        {/* Tag */}

                                        {car.tag && (
                                            <>
                                                {car.tag.name != "Vehículo por ingresar" ? (
                                                    <div
                                                        className="tag"
                                                        style={{
                                                            backgroundColor: car.tag.color,
                                                        }}
                                                    >
                                                        <p
                                                            className="tag-text"
                                                            style={{
                                                                color:
                                                                    car.tag.name === "Vendido"
                                                                        ? "#000"
                                                                        : car.tag.color === "#000"
                                                                        ? "#fff"
                                                                        : "#fff",
                                                            }}
                                                        >
                                                            {car.tag.name}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="tag"
                                                        style={{
                                                            backgroundColor: "#E49B0F",
                                                        }}
                                                    >
                                                        <p
                                                            className="tag-text"
                                                            style={{
                                                                backgroundColor: "#E49B0F",
                                                                color: "#fff",
                                                            }}
                                                        >
                                                            Vehículo por ingresar
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {images[0] === image && car.warranty && (
                                            <div className="stamp">
                                                <img src="/assets/icons/Stamp.svg" alt={car?.name} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Carousel>
                </div>

                <div className="arrows_container">
                    <div className={`arrow_slider previous me-1 ${images.length <= 1 ? "disabled" : ""}`}>
                        <AiOutlineArrowLeft onClick={_handlePrevImage} />
                    </div>
                    <div className={`arrow_slider next ms-1 ${images.length <= 1 ? "disabled" : ""}`}>
                        <AiOutlineArrowRight onClick={_handleNextImage} />
                    </div>
                </div>
            </div>

            <div className="images_thumbails mt-3">
                {images.map((image, index) => (
                    <div
                        className={`carousel_thumbail ${imageShowing == index ? "active" : ""}`}
                        key={index}
                        onClick={() => _handleGoToImage(index)}
                        style={{ padding: 0 }}
                    >
                        <ThumbnailImage
                            src={image}
                            srcSet={buildSrcSet(variantsFor(car.imageSrcsetByKey, image))}
                            alt={car?.name ?? ""}
                        />
                    </div>
                ))}
            </div>

            {/* Solo mobile: en desktop el boton va en la lista de beneficios (DetailsList). */}
            {/* 12px arriba (10px del margen de la miniatura + 2) y 12px abajo: el -36 le resta al mt-5 (48px) de la tarjeta. */}
            <div className="d-lg-none" style={{ marginTop: 2, marginBottom: -36 }}>
                <VideoButton car={car} />
            </div>
        </div>
    );
};

export default CarCarouselImagesComponent;
